import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/customSupabaseClient';
import { Star, Upload, Calendar, User, Mail, MessageSquare, CheckCircle, AlertCircle } from 'lucide-react';
import environmentConfig from '../config/environment.js';

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const Reviews = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    rating: 5,
    review: '',
    serviceType: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleRatingChange = (rating) => {
    setFormData(prev => ({
      ...prev,
      rating: rating
    }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast({
          variant: "destructive",
          title: "File Too Large",
          description: "Please select an image smaller than 5MB.",
        });
        return;
      }
      setUploadedImage(file);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    if (!formData.rating || formData.rating < 1 || formData.rating > 5) {
      errors.rating = 'Please select a rating';
    }
    
    if (!formData.review.trim()) {
      errors.review = 'Review is required';
    } else if (formData.review.trim().length < 10) {
      errors.review = 'Review must be at least 10 characters long';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast({
        variant: "destructive",
        title: "Please fix the errors below",
        description: "Some required fields are missing or invalid.",
      });
      return;
    }
    
    setIsSubmitting(true);

    try {
      const { error } = await supabase
        .from('reviews')
        .insert([{
          name: formData.name,
          email: formData.email,
          rating: parseInt(formData.rating),
          review_text: formData.review,
          service_type: formData.serviceType || null
        }]);

      if (error) {
        console.error('Supabase error:', error);
        toast({
          variant: "destructive",
          title: "Submission Failed",
          description: error.message || "There was an error submitting your review. Please try again.",
        });
      } else {
        toast({
          title: "Review Submitted!",
          description: "Thank you for your feedback! Your review will be reviewed before being published.",
        });
        setFormData({
          name: '',
          email: '',
          rating: 5,
          review: '',
          serviceType: ''
        });
        setUploadedImage(null);
        setValidationErrors({});
      }
    } catch (error) {
      console.error('Unexpected error:', error);
      toast({
        variant: "destructive",
        title: "Submission Failed",
        description: "There was an unexpected error. Please try again later.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Leave a Review - Akiblackcar</title>
        <meta name="description" content="Leave a review for your experience with Akiblackcar." />
      </Helmet>
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeIn}
        className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 flex items-center justify-center"
      >
        <Card className="w-full max-w-lg bg-brand-charcoal border-brand-gold/20 p-8">
          <CardHeader>
            <CardTitle className="text-3xl font-serif text-white text-center">Leave Your Review</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmitReview} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name" className="text-lg">Your Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="mt-2 h-12 text-lg"
                  />
                  {validationErrors.name && (
                    <p className="text-red-400 text-sm mt-1">{validationErrors.name}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="email" className="text-lg">Your Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="mt-2 h-12 text-lg"
                  />
                  {validationErrors.email && (
                    <p className="text-red-400 text-sm mt-1">{validationErrors.email}</p>
                  )}
                </div>
              </div>

              <div>
                <Label className="text-lg">Rating *</Label>
                <div className="flex space-x-2 mt-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => handleRatingChange(star)}
                      onMouseEnter={() => setHoveredRating(star)}
                      onMouseLeave={() => setHoveredRating(0)}
                      className="text-4xl transition-colors duration-200"
                    >
                      <span className={`${(hoveredRating || formData.rating) >= star ? 'text-yellow-400' : 'text-gray-600'}`}>
                        ★
                      </span>
                    </button>
                  ))}
                </div>
                <p className="text-sm text-gray-400 mt-1">
                  {formData.rating === 1 && 'Poor'}
                  {formData.rating === 2 && 'Fair'}
                  {formData.rating === 3 && 'Good'}
                  {formData.rating === 4 && 'Very Good'}
                  {formData.rating === 5 && 'Excellent'}
                </p>
                {validationErrors.rating && (
                  <p className="text-red-400 text-sm mt-1">{validationErrors.rating}</p>
                )}
              </div>

              <div>
                <Label htmlFor="serviceType" className="text-lg">Service Type (Optional)</Label>
                <Select
                  value={formData.serviceType}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, serviceType: value }))}
                >
                  <SelectTrigger className="mt-2 h-12 text-lg">
                    <SelectValue placeholder="Select service type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="airport">Airport Transfer</SelectItem>
                    <SelectItem value="city">City Transfer</SelectItem>
                    <SelectItem value="event">Event Transportation</SelectItem>
                    <SelectItem value="wedding">Wedding Service</SelectItem>
                    <SelectItem value="corporate">Corporate Transportation</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="review" className="text-lg">Your Review *</Label>
                <Textarea
                  id="review"
                  name="review"
                  placeholder="Tell us about your experience..."
                  rows="5"
                  value={formData.review}
                  onChange={handleInputChange}
                  required
                  className="mt-2 text-lg"
                />
                {validationErrors.review && (
                  <p className="text-red-400 text-sm mt-1">{validationErrors.review}</p>
                )}
              </div>

              <Button type="submit" size="lg" className="w-full bg-brand-gold text-brand-dark hover:bg-yellow-500 font-bold text-xl py-7" disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Submit Review'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </motion.div>
    </>
  );
};

export default Reviews;